import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom'; // IMPORT AGGIUNTO
import './Uscite.css';
import Calendar from '../DatePanel/Calendar'; // Importiamo il Calendar
import { formatDateRange } from '../../utils/formatDateRange'; // NUOVA IMPORTAZIONE
import { usciteService, Uscita, LocalitaFeature } from '../../services/api'; // IMPORTATO usciteService e Uscita
import { MapLocationSet } from '../../types/mapTypes'; // MODIFICATO: Import da nuovo percorso e nuovo nome interfaccia
import { seedManifestFromGetFlat, syncManifestIncremental } from '../../services/manifestSync';
import { onWriteSuccess } from '../../services/writeQueue';

interface WidgetProps {
  onClose: () => void;
  isAdmin?: boolean;
  className?: string;
  onUscitaSelected?: (uscita: Uscita | Uscita[] | null) => void;
  onUsciteCountChange?: (count: number) => void;
  skipFetch?: boolean;
  preloadedUscite?: Uscita[];
}

const DEBOUNCE_DELAY = 800; // ms
const MAX_RANGE_DAYS = 7; // Massimo numero di giorni per il range

const Uscite: React.FC<WidgetProps> = ({ onClose, isAdmin, className, onUscitaSelected, onUsciteCountChange, skipFetch, preloadedUscite }) => {
  const [dataDal, setDataDal] = useState<Date | null>(new Date()); 
  const [dataAl, setDataAl] = useState<Date | null>(new Date());   
  const [usciteData, setUsciteData] = useState<Uscita[]>([]); // MODIFICATO: da DisplayUscitaData[] a Uscita[]
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Rimossi stati per calendari individuali: showIndividualCalendarFor, individualCalendarStyle

  // Nuovi stati per il calendario di range
  const [isRangeCalendarOpen, setIsRangeCalendarOpen] = useState<boolean>(false);
  const [rangeSelectionStep, setRangeSelectionStep] = useState<'start' | 'end'>('start');
  const [tempRangeStartDate, setTempRangeStartDate] = useState<Date | null>(null);
  const [rangeCalendarStyle, setRangeCalendarStyle] = useState<React.CSSProperties>({});
  // Nuovo stato per controllare la finalizzazione della selezione del range
  const [isRangeSelectionFinalized, setIsRangeSelectionFinalized] = useState<boolean>(true); // Inizializza a true per il primo caricamento con date di default

  // NUOVI STATI PER LA SELEZIONE NELLA LISTA
  const [selectedDataKey, setSelectedDataKey] = useState<string | null>(null);
  const [selectedUscitaId, setSelectedUscitaId] = useState<number | null>(null);

  // Stato per tracciare se l'auto-selezione per il dataset corrente è stata fatta
  const [autoSelectDoneForCurrentData, setAutoSelectDoneForCurrentData] = useState(false);

  const rangeDisplayRef = useRef<HTMLButtonElement>(null); // Ref per il pulsante/display del range

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // HANDLER PER LA SELEZIONE DEI GRUPPI DATA
  const handleDataGroupClick = useCallback((dataKey: string) => {
    const newSelectedDataKey = selectedDataKey === dataKey ? null : dataKey;
    setSelectedDataKey(newSelectedDataKey);
    setSelectedUscitaId(null); 
    console.log("[UsciteWidget] Gruppo data selezionato:", newSelectedDataKey);
    
    // Se l'utente deseleziona tutto, resetta il flag di auto-selezione per permettere una nuova auto-selezione futura
    if (newSelectedDataKey === null) {
      setAutoSelectDoneForCurrentData(false);
    }
    
    if (onUscitaSelected) {
      if (newSelectedDataKey) {
        const usciteDelGiorno = usciteData.filter(u => u.mob_usc_data === newSelectedDataKey);
        onUscitaSelected(usciteDelGiorno.length > 0 ? usciteDelGiorno : null); 
      } else {
        onUscitaSelected(null); 
      }
    }
  }, [selectedDataKey, usciteData, onUscitaSelected]);

  // HANDLER PER LA SELEZIONE DELLE RIGHE USCITA
  const handleUscitaRowClick = useCallback((uscita: Uscita) => {
    const newSelectedUscitaId = selectedUscitaId === uscita.mob_usc_id ? null : uscita.mob_usc_id;
    setSelectedUscitaId(newSelectedUscitaId);
    setSelectedDataKey(null); 
    console.log("[UsciteWidget] Uscita selezionata per evidenziazione:", newSelectedUscitaId);

    // Se l'utente deseleziona tutto, resetta il flag di auto-selezione per permettere una nuova auto-selezione futura
    if (newSelectedUscitaId === null) {
      setAutoSelectDoneForCurrentData(false);
    }

    if (onUscitaSelected) {
      if (newSelectedUscitaId) {
        const selectedUscitaObj = usciteData.find(u => u.mob_usc_id === newSelectedUscitaId);
        onUscitaSelected(selectedUscitaObj || null); 
      } else {
        onUscitaSelected(null); 
      }
    }
  }, [selectedUscitaId, usciteData, onUscitaSelected]);

  // Funzione helper per calcolare il bounding box (simile a WinUscite)
  const calculateBoundingBoxForWidget = (features: LocalitaFeature[]): MapLocationSet['bounds'] | null => {
    if (!features || features.length === 0) return null;
    
    let north = -90, south = 90, east = -180, west = 180;
    let hasValidCoords = false;

    features.forEach(feature => {
      if (feature.geometry && feature.geometry.coordinates && feature.geometry.coordinates[0] && feature.geometry.coordinates[0][0]) {
        feature.geometry.coordinates[0][0].forEach(([lon, lat]) => {
          if (typeof lat === 'number' && typeof lon === 'number') {
            north = Math.max(north, lat);
            south = Math.min(south, lat);
            east = Math.max(east, lon);
            west = Math.min(west, lon);
            hasValidCoords = true;
          }
        });
      }
    });
    return hasValidCoords ? { north, south, east, west } : null;
  };

  const formatDateForDisplayInList = (dateString: string): string => {
    if (!dateString) return 'Data sconosciuta';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      // year: 'numeric' // Rimosso l'anno per brevità nel widget
    });
  };

  const groupUsciteByDate = useCallback(() => {
    const result: { [key: string]: Uscita[] } = {}; // MODIFICATO: DisplayUscitaData[] a Uscita[]
    usciteData.forEach(uscita => {
      if (uscita && uscita.mob_usc_data) { // MODIFICATO: usa direttamente i campi di Uscita
        const dataUsc = uscita.mob_usc_data;
        if (!result[dataUsc]) {
          result[dataUsc] = [];
        }
        result[dataUsc].push(uscita);
      }
    });
    // Ordina le date E le uscite dentro ogni data per gruppo_num
    const orderedResult: { [key: string]: Uscita[] } = {}; // MODIFICATO: DisplayUscitaData[] a Uscita[]
    Object.keys(result).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()).forEach(key => {
      // Ordina le uscite di questa data per gruppo_num
      orderedResult[key] = result[key].sort((a, b) => a.mob_usc_num - b.mob_usc_num);
    });
    return orderedResult;
  }, [usciteData]);

  const fetchUscite = useCallback(async (start: Date, end: Date) => {
    setIsLoading(true);
    setError(null);
    
    // RESET IMMEDIATO delle selezioni quando inizia un nuovo fetch
    console.log("[UsciteWidget] FETCH INIZIATO - Reset selezioni");
    setSelectedUscitaId(null);
    setSelectedDataKey(null);
    setAutoSelectDoneForCurrentData(false);
    
    // Notifica il componente padre per resettare mappa e dettaglio
    if (onUscitaSelected) {
      onUscitaSelected(null);
    }
    
    console.log(`[UsciteWidget] Fetching uscite da ${start.toISOString().split('T')[0]} a ${end.toISOString().split('T')[0]}`);
    const startTime = performance.now(); // Inizio misurazione

    try {
      const response = await usciteService.getUscite(start, end);
      const endTime = performance.now(); // Fine misurazione
      console.log(`[UsciteWidget] Chiamata API getUscite completata in ${(endTime - startTime).toFixed(2)} ms`); // Log del tempo

      if (response.error) {
        console.error('[UsciteWidget] Errore dalla API:', response.error);
        let errorMessage = 'Errore nel recupero delle uscite.'; // Default error message
        if (typeof response.error === 'string') {
          errorMessage = response.error;
        } else if (response.error && typeof (response.error as any).message === 'string') {
          // Assicurati che response.error sia un oggetto e abbia una proprietà message di tipo stringa
          errorMessage = (response.error as any).message;
        }
        setError(errorMessage);
        setUsciteData([]);
      } else if (response.data && response.data.uscite) {
        console.log('[UsciteWidget] Dati ricevuti:', response.data.uscite.length);
        // Ordina le uscite per gruppo_num per consistenza con la visualizzazione
        const usciteOrdinate = response.data.uscite.sort((a, b) => a.mob_usc_num - b.mob_usc_num);
        console.log('[UsciteWidget] Uscite ordinate per gruppo_num');
        // La geometria arriva già inclusa in get-flat (mob_json_geom su pwa_uscloc, lookup 1:1 su
        // Comuni) — nessuna chiamata separata, nessun doppio aggiornamento dello stato.
        setUsciteData(usciteOrdinate);
        // Pattern manifest+cache: fotografa lo stato appena arrivato (snapshot leggero + dati
        // pieni) come base per i sync incrementali successivi — fire-and-forget, un fallimento
        // qui non deve mai impedire di mostrare i dati già arrivati da get-flat.
        void seedManifestFromGetFlat(usciteOrdinate).catch((err) => {
          console.error('[UsciteWidget] Errore seed manifest da get-flat:', err);
        });
      } else {
        console.warn('[UsciteWidget] Nessun dato o errore non gestito nella risposta.');
        setError('Nessun dato ricevuto o formato risposta inatteso.');
        setUsciteData([]);
      }
    } catch (err: any) {
      const endTime = performance.now(); // Fine misurazione anche in caso di errore
      console.log(`[UsciteWidget] Chiamata API getUscite fallita dopo ${(endTime - startTime).toFixed(2)} ms`); // Log del tempo
      console.error('[UsciteWidget] Eccezione durante il fetch:', err, JSON.stringify(err.response?.data));
      let displayError = 'Errore di connessione o durante la richiesta.';
      if (err.response && err.response.data) {
        if (typeof err.response.data.message === 'string') {
          displayError = err.response.data.message;
        } else if (typeof err.response.data.error === 'string') {
          displayError = err.response.data.error;
        } else if (err.response.data.error && typeof err.response.data.error.message === 'string') {
          displayError = err.response.data.error.message;
        } else if (typeof err.response.data === 'string') {
          displayError = err.response.data;
        }
      } else if (err.message) {
        displayError = err.message;
      }
      setError(displayError);
      setUsciteData([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // fetchUscite non dovrebbe cambiare spesso

  // Sessioni QR: preloadedUscite sostituisce il fetch FM (fmToken non disponibile).
  useEffect(() => {
    if (skipFetch && preloadedUscite && preloadedUscite.length > 0) {
      setUsciteData(preloadedUscite);
    }
  }, [skipFetch, preloadedUscite]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // NUOVO useEffect per notificare il cambiamento del conteggio uscite
  useEffect(() => {
    if (onUsciteCountChange) {
      const count = usciteData.length;
      console.log('[UsciteWidget] Notifica cambiamento conteggio uscite:', count);
      onUsciteCountChange(count);
    }
  }, [usciteData, onUsciteCountChange]);

  // PATTERN MANIFEST+CACHE — sync incrementale in background: non sostituisce mai il
  // caricamento principale via get-flat sopra (che resta l'unica fonte per il primo
  // caricamento/cambio range), lo integra per tenere i dati aggiornati senza riscaricare tutto.
  // Se qualcosa è davvero cambiato, ricompone la lista dalla cache. La ri-notifica della
  // selezione attiva (mappa/widget dettaglio) avviene SOLO se l'uscita/il giorno attualmente in
  // vista è tra quelli davvero toccati da questo giro (touchedUscIds) — altrimenti si otterrebbero
  // nuovi riferimenti oggetto per dati identici, che fanno ricostruire inutilmente il layer
  // poligoni su Leaflet (il "piccolo refresh mappa" osservato dal vivo il 2026-07-09).
  const runIncrementalSync = useCallback(async () => {
    if (!dataDal || !dataAl) return;
    try {
      const result = await syncManifestIncremental(dataDal, dataAl);
      if (!result) return; // nulla di cambiato, o fetch fallito — non tocco lo stato esistente

      const { uscite: updated, touchedUscIds } = result;
      const ordinate = [...updated].sort((a, b) => a.mob_usc_num - b.mob_usc_num);
      setUsciteData(ordinate);

      if (onUscitaSelected) {
        if (selectedUscitaId !== null) {
          if (touchedUscIds.has(selectedUscitaId)) {
            const fresh = ordinate.find((u) => u.mob_usc_id === selectedUscitaId);
            if (fresh) onUscitaSelected(fresh);
          }
        } else if (selectedDataKey !== null) {
          const freshDelGiorno = ordinate.filter((u) => u.mob_usc_data === selectedDataKey);
          const giornoToccato = freshDelGiorno.some((u) => touchedUscIds.has(u.mob_usc_id));
          if (giornoToccato && freshDelGiorno.length > 0) onUscitaSelected(freshDelGiorno);
        }
      }
    } catch (err) {
      console.error('[UsciteWidget] Errore durante il sync incrementale manifest:', err);
    }
  }, [dataDal, dataAl, selectedUscitaId, selectedDataKey, onUscitaSelected]);

  // Il timer non deve resettarsi ogni volta che cambia la selezione (runIncrementalSync cambia
  // identità a ogni click) — la logica di lavoro vera resta sempre fresca tramite il ref, la vita
  // del timer dipende solo da skipFetch e dal primo caricamento dati.
  const latestSyncRef = useRef(runIncrementalSync);
  useEffect(() => { latestSyncRef.current = runIncrementalSync; }, [runIncrementalSync]);

  const hasData = usciteData.length > 0;
  useEffect(() => {
    if (skipFetch) return; // sessioni QR: niente fmToken personale, manifest/fetch mirato falliscono sempre
    if (!hasData) return; // aspetta il primo caricamento completo (get-flat) prima di partire

    let raw: unknown;
    try {
      const stored = sessionStorage.getItem('userDetails');
      raw = stored ? JSON.parse(stored)?.mob_user_update_timesec : undefined;
    } catch {
      raw = undefined;
    }
    const intervalSec = Number(raw) > 0 ? Number(raw) : 300; // default se assente/non valido
    const intervalMs = Math.max(30, intervalSec) * 1000; // minimo 30s, non martellare per errore

    const timer = setInterval(() => { void latestSyncRef.current(); }, intervalMs);
    const unsubscribeWrite = onWriteSuccess(() => { void latestSyncRef.current(); });

    // In background (mobile: schermo bloccato, app switch) i browser sospendono setInterval —
    // su iOS/Android il JS smette proprio di girare, e al ritorno il timer non recupera i giri
    // persi, riparte da lì in avanti. Un capogruppo che sblocca il telefono dopo una pausa
    // vedrebbe dati fermi fino al prossimo giro naturale (fino a intervalSec di ritardo).
    // Sincronizzare subito al ritorno in foreground chiude questo buco.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void latestSyncRef.current();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timer);
      unsubscribeWrite();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [skipFetch, hasData]);

  useEffect(() => {
    if (skipFetch) return;
    if (dataDal && dataAl && isRangeSelectionFinalized) {
      if (dataDal > dataAl) {
        setUsciteData([]);
        console.warn("[UsciteWidget] Data Dal successiva a Data Al, fetch non eseguito.");
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        // Anche se le date sono invalide, potremmo voler resettare i widget figli
        if (onUscitaSelected) {
          onUscitaSelected(null);
        }
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Resetta lo stato dei widget dipendenti prima di avviare il fetch per il nuovo range
      // Nota: il reset viene gestito dal useEffect specifico per il cambio date più sotto

      debounceTimerRef.current = setTimeout(() => {
        fetchUscite(dataDal, dataAl);
        setIsRangeSelectionFinalized(false); // Resetta per la prossima selezione di range
      }, DEBOUNCE_DELAY);
    } else {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Se le date non sono valide o la selezione non è finalizzata,
      // potremmo anche voler resettare, ma per ora lo facciamo solo su finalizzazione.
      // Considera se è necessario un reset anche qui in altri scenari.
    }
  }, [dataDal, dataAl, isRangeSelectionFinalized, fetchUscite, onUscitaSelected]); // Aggiunto onUscitaSelected alle dipendenze

  // Effetto per resettare il flag di auto-selezione quando le date cambiano
  useEffect(() => {
    // Per sessioni QR non fare nulla: l'uscita è già impostata dal parent, non deve essere resettata
    if (skipFetch) return;

    console.log("[UsciteWidget] Date cambiate, resetto autoSelectDoneForCurrentData e selezioni.");

    setAutoSelectDoneForCurrentData(false);
    setSelectedUscitaId(null);
    setSelectedDataKey(null);

    if (onUscitaSelected) {
      onUscitaSelected(null);
    }

  }, [dataDal, dataAl, onUscitaSelected, skipFetch]);

  // useEffect per l'auto-selezione della prima uscita (Opzione 3)
  // Esegue l'auto-selezione solo se non c'è già una selezione attiva
  useEffect(() => {
    if (!isLoading && !error && usciteData && usciteData.length > 0 && !autoSelectDoneForCurrentData && selectedUscitaId === null) {
      const primaUscita = usciteData[0];
      console.log(`[UsciteWidget] Auto-selezione iniziale della prima uscita: ${primaUscita.mob_usc_id} - ${primaUscita.gruppo_cg_nome}`);
      handleUscitaRowClick(primaUscita);
      setAutoSelectDoneForCurrentData(true); 
    }
  }, [usciteData, isLoading, error, autoSelectDoneForCurrentData, selectedUscitaId, handleUscitaRowClick]);

  // Rimosse funzioni per calendari individuali: handleOpenIndividualCalendar, handleIndividualCalendarSelect

  const handleOpenRangeCalendar = (event?: React.MouseEvent<HTMLElement>) => {
    // Usa il ref del pulsante per posizionare il calendario se l'evento non è disponibile (es. chiamato da altra logica)
    const targetElement = event ? event.currentTarget : rangeDisplayRef.current;
    if (!targetElement) return;

    setRangeSelectionStep('start');
    setTempRangeStartDate(null); 
    setIsRangeSelectionFinalized(false); 
    // NON apriamo il calendario qui: setIsRangeCalendarOpen(true);

    // Ritardiamo il calcolo e l'apertura per consentire al browser di eseguire il reflow
    requestAnimationFrame(() => {
      const rect = targetElement.getBoundingClientRect();
      setRangeCalendarStyle({
        position: 'fixed',
        top: `${rect.bottom + window.scrollY + 2}px`, 
        left: `${rect.left + window.scrollX}px`,
        zIndex: 1600 // Assicurati che sia adeguato rispetto agli altri z-index (es. hamburger button)
      });
      setIsRangeCalendarOpen(true); // Apriamo il calendario solo dopo aver impostato lo stile
    });
  };

  const handleRangeCalendarSelect = (selectedDate: Date) => {
    if (rangeSelectionStep === 'start') {
      setTempRangeStartDate(selectedDate);
      setDataDal(selectedDate); 
      setRangeSelectionStep('end');
    } else if (rangeSelectionStep === 'end' && tempRangeStartDate) {
      const diffTime = Math.abs(selectedDate.getTime() - tempRangeStartDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      if (diffDays > MAX_RANGE_DAYS) {
        setError(`L'intervallo massimo selezionabile è di ${MAX_RANGE_DAYS} giorni.`);
        return;
      } else {
        setError(null);
      }
      
      if (selectedDate < tempRangeStartDate) {
        setDataDal(selectedDate);
        setDataAl(tempRangeStartDate);
      } else {
        setDataAl(selectedDate);
      }
      setIsRangeCalendarOpen(false);
      setRangeSelectionStep('start'); 
      setTempRangeStartDate(null); 
      setIsRangeSelectionFinalized(true); 
    }
  };



  const widgetTitle = "Uscite Programmate";

  // Props per il calendario di range
  const rangeCalendarMinDate = rangeSelectionStep === 'end' && tempRangeStartDate ? tempRangeStartDate : null;
  let rangeCalendarMaxDate = null;
  if (rangeSelectionStep === 'end' && tempRangeStartDate) {
    rangeCalendarMaxDate = new Date(tempRangeStartDate.getTime() + (MAX_RANGE_DAYS - 1) * 24 * 60 * 60 * 1000);
  }

  const rangeDisplayText = formatDateRange(dataDal, dataAl);

  // DEBUG LOG PER STATO
  console.log("[UsciteWidget] RENDER, isLoading:", isLoading, "error:", error, "usciteData.length:", usciteData.length);
  console.log("[UsciteWidget] RENDER, className:", className);
  console.log("[UsciteWidget] RENDER, selectedUscitaId:", selectedUscitaId, "selectedDataKey:", selectedDataKey); // NUOVO DEBUG

  return (
    <div className={`widget-card uscite-widget ${className || ''}`}>
      <div className="widget-header">
        <h3>{widgetTitle}</h3>
        <button onClick={onClose} className="widget-close-btn" aria-label="Chiudi widget Uscite">
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="widget-content">
        {/* Rimosso uscite-widget-content-wrapper e uscite-list-panel */}
        {/* Il subheader, i messaggi e la lista diventano figli diretti di widget-content */}
        {isAdmin && (
          <div className="uscite-widget-subheader simplified">
            <button 
              ref={rangeDisplayRef}
              onClick={handleOpenRangeCalendar} 
              className="range-calendar-button-simplified"
              title="Seleziona intervallo date"
            >
              <i className="fas fa-calendar-alt"></i>
              <span className="range-display-text">{rangeDisplayText}</span>
            </button>
          </div>
        )}

        {isLoading && <p className="widget-message loading">Caricamento uscite...</p>}
        {!isLoading && error && <p className="widget-message error">{error}</p>}
        {!isLoading && !error && usciteData.length === 0 && (
          <p className="widget-message info">
            {isAdmin || (!isAdmin && (dataDal && dataAl)) 
              ? "Nessuna uscita trovata." 
              : "Dati uscite non disponibili."
            }
          </p>
        )}

        {!isLoading && !error && usciteData.length > 0 && (
          <div className="uscite-list-widget-container"> 
            {Object.entries(groupUsciteByDate()).map(([dataUsc, usciteDelGiorno]) => (
              <div key={dataUsc} className="usc-datausc-group-widget">
                <h2 
                  className={`usc-datausc-header-widget ${selectedDataKey === dataUsc ? 'selected' : ''}`} 
                  onClick={() => handleDataGroupClick(dataUsc)}
                >
                  {formatDateForDisplayInList(dataUsc)}
                </h2>
                <ul className="uscite-items-for-date-widget">
                  {usciteDelGiorno.map(uscita => (
                    <li 
                      key={uscita.mob_usc_id}
                      className={`uscita-item-widget ${selectedUscitaId === uscita.mob_usc_id ? 'selected' : ''}`}
                      onClick={() => {
                        handleUscitaRowClick(uscita);
                      }}
                    >
                      <div className="uscita-item-content">
                        <span className="gruppo-num-widget">{uscita.mob_usc_num}</span>
                        <span className="gruppo-nome-widget">{uscita.gruppo_cg_nome}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {isRangeCalendarOpen && document.body &&
        ReactDOM.createPortal(
          <Calendar
            selectedDate={rangeSelectionStep === 'start' ? (dataDal || new Date()) : (dataAl || dataDal || new Date())}
            onDateSelect={handleRangeCalendarSelect}
            onClose={() => {
              setIsRangeCalendarOpen(false);
              setRangeSelectionStep('start'); 
              setTempRangeStartDate(null);
              if (!dataDal || !dataAl) { 
                setIsRangeSelectionFinalized(false); 
              } else if(tempRangeStartDate && rangeSelectionStep === 'end'){ 
                 setIsRangeSelectionFinalized(false);
              } else {
                 // Non fare nulla per mantenere isRangeSelectionFinalized a true se le date erano già valide e finalizzate
              }
            }}
            style={rangeCalendarStyle}
            minSelectableDate={rangeCalendarMinDate}
            maxSelectableDate={rangeCalendarMaxDate}
          />,
          document.body
        )
      }
    </div>
  );
};

export default Uscite; 